import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ValidationErrors } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { CustomerService } from '../../../services/customer.service';
import { GlobalsService } from '../../../services/globals.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { DispatchService } from '../../../services/dispatch.service';
import { SystemService } from '../../../services/system.service';
import { UsersService } from '../../../services/users.service';
import { customerBranchEmailValidators, isCustomerEmailRequired } from '../../../shared/customer-email-config';

/** Fills gaps in `config.defaults` (KM/PRO JSON does not list these). */
const CUSTOMER_APPROVAL_MINIMAL_FALLBACK: Record<string, any> = {
	cosm_license: '',
	licExpDate: '',
	signed_mor: false,
};

/** Used only when the loaded client config has no `defaults` object (e.g. config.standard.json). */
const CUSTOMER_APPROVAL_LEGACY_FALLBACK: Record<string, any> = {
	country: 'United States',
	region: 'NY',
	holdreason: '1',
	currency: 'USD',
	terms: 'COD',
	salestype: 'A',
	customertype: '1',
	discount: 0,
	credit: 0,
	email_invoice: false,
	requirepo: false,
	email_statement: false,
	interest: 3,
	warehouse: '00',
	shipvia: '1',
	area: '01',
	territory: '01',
	salesman: '',
	disabled: false,
	tirefee: true,
	taxauth: '119',
	branchcode: '00',
	default_paymentmehod: '2',
	cosm_license: '',
	licExpDate: '',
	signed_mor: false,
};

export interface WebCustomerQueues {
	awaiting: any[];
	denied: any[];
}

@Component({
	selector: 'app-customer-approval',
	templateUrl: './customer-approval.component.html',
	styleUrls: ['./customer-approval.component.scss'],
})
export class CustomerApprovalComponent implements OnInit, OnDestroy {
	get customerEmailRequired(): boolean {
		return isCustomerEmailRequired(this.config);
	}
	@ViewChild('detailsModal') detailsRef: ElementRef;
	@ViewChild('approveModal') approveRef: ElementRef;
	@ViewChild('stepper', { static: true }) stepper: MatStepper;
	@ViewChild('detailsModal2') detailsModalEle: ElementRef;

	@Output() customersawaiting = new EventEmitter<WebCustomerQueues>();

	private readonly destroy$ = new Subject<void>();
	private branchFormSubs = new Subscription();

	debtorFromGroup: UntypedFormGroup;
	branchFormGroup: UntypedFormGroup;
	contactFormGroup: UntypedFormGroup;

	searchVal = new UntypedFormControl('');
	awaitingSearch = new UntypedFormControl('');
	deniedSearch = new UntypedFormControl('');

	isEditable = false;
	emailwarn = false;
	terms: any[] = [];
	customertypes: any[] = [];
	holdreasons: any[] = [];
	currencies: any[] = [];
	salestypes: any[] = [];
	zones: any = null;
	taxes: any[] = [];
	alltaxes: any[] = [];
	locations: any[] = [];
	areas: any[] = [];
	salesman: any[] = [];
	shipvia: any[] = [];
	paymentmethods: any[] = [];
	routes: any[] = [];
	contacttypes: any[] = [];

	defaults: Record<string, any> = { ...CUSTOMER_APPROVAL_LEGACY_FALLBACK };

	color = '';
	config: any = null;
	duplicatewarning: any[] = [];
	modalAction: boolean;
	contacts: any[] = [];
	customers: WebCustomerQueues = { awaiting: [], denied: [] };
	allcustomers: WebCustomerQueues = { awaiting: [], denied: [] };
	detail: any = null;
	approvingNew = false;
	custresults: any[] = [];
	pendingWebCustomer: any = null;
	place: any = false;
	phone = '';
	customer_created: any = [];
	isAssigning = false;
	searching = false;
	listRefreshLoading = false;
	userData: any;

	constructor(
		public systemService: SystemService,
		private userService: UsersService,
		public dispatchService: DispatchService,
		private fb: UntypedFormBuilder,
		public globalsService: GlobalsService,
		public customerService: CustomerService,
		private globalSearchService: GlobalSearchService,
		private modalService: NgbModal,
	) {}

	ngOnInit(): void {
		this.userData = this.userService.getLocalUser();

		this.mergeAppConfigDefaults();

		this.refreshApprovalList();

		this.globalSearchService.configsubscription.pipe(takeUntil(this.destroy$)).subscribe((c) => {
			this.config = c;
			const cd = c?.defaults;
			if (cd && typeof cd === 'object' && Object.keys(cd).length > 0) {
				this.defaults = { ...this.defaults, ...cd };
			}
			this.overlayRegionalDefaultsFromConfig();
			this.patchBranchEmailValidators();
		});

		this.color = this.globalSearchService.getColor();

		this.dispatchService.getRoutes().pipe(takeUntil(this.destroy$)).subscribe((r: any) => {
			this.routes = r;
		});

		this.globalSearchService.taxgroups.pipe(takeUntil(this.destroy$)).subscribe((results: any) => {
			this.taxes = results || [];
			this.alltaxes = results || [];
		});

		this.globalSearchService.salespeople.pipe(takeUntil(this.destroy$)).subscribe((results: any) => {
			this.salesman = results || [];
		});

		this.globalSearchService.shipvia.pipe(takeUntil(this.destroy$)).subscribe((results: any) => {
			this.shipvia = results || [];
		});

		this.globalSearchService.terms.pipe(takeUntil(this.destroy$)).subscribe((results: any) => {
			this.terms = results || [];
		});

		this.globalSearchService.zones.pipe(takeUntil(this.destroy$)).subscribe((results: any) => {
			this.zones = results;
		});

		this.globalSearchService.holdreasons.pipe(takeUntil(this.destroy$)).subscribe((results: any) => {
			this.holdreasons = results || [];
		});

		this.globalSearchService.locations.pipe(takeUntil(this.destroy$)).subscribe((results: any) => {
			this.locations = results || [];
		});

		this.globalSearchService.customertypes.pipe(takeUntil(this.destroy$)).subscribe((details) => {
			this.customertypes = details || [];
		});

		this.globalSearchService.currencies.pipe(takeUntil(this.destroy$)).subscribe((details) => {
			this.currencies = details || [];
		});

		this.globalSearchService.salestypes.pipe(takeUntil(this.destroy$)).subscribe((details) => {
			this.salestypes = details || [];
		});

		this.globalSearchService.areas.pipe(takeUntil(this.destroy$)).subscribe((details) => {
			this.areas = details || [];
		});

		this.globalsService.getPaymentMethods().pipe(takeUntil(this.destroy$)).subscribe((details: any) => {
			this.paymentmethods = Array.isArray(details) ? details : [];
		});

		this.globalsService.getTypes().pipe(takeUntil(this.destroy$)).subscribe((results: any) => {
			this.globalSearchService.all_system_types.next(results);
			this.contacttypes = results?.contacttypes || [];
			this.contactFormGroup = this.fb.group({
				contid: [0],
				name: ['', Validators.required],
				email: ['', [Validators.email]],
				phone: [''],
				role: [[], Validators.required],
			});
			this.contactFormGroup
				.get('email')
				?.valueChanges.pipe(takeUntil(this.destroy$))
				.subscribe((em: string) => {
					const c = this.contactFormGroup.get('email');
					this.emailwarn = !!(c?.value && c.invalid && (c.dirty || c.touched));
				});
		});

		this.setForms();

		this.debtorFromGroup.get('paymentterms')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newValue) => {
			if (this.terms?.length && newValue) {
				const termcode = this.terms.find((t) => t.termsindicator === newValue);
				if (termcode?.collect_payment === '1') {
					this.debtorFromGroup.get('creditlimit')?.setValue('0.00');
				}
			}
		});

		this.debtorFromGroup.get('emailstatement')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newValue: any) => {
			const c = this.debtorFromGroup.get('statementemailaddress');
			if (newValue) {
				c?.setValidators([Validators.required, Validators.email]);
			} else {
				c?.clearValidators();
			}
			c?.updateValueAndValidity();
		});

		this.debtorFromGroup.get('ediinvoices')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newValue: any) => {
			const c = this.debtorFromGroup.get('invoiceemailaddress');
			if (newValue) {
				c?.setValidators([Validators.required, Validators.email]);
			} else {
				c?.clearValidators();
			}
			c?.updateValueAndValidity();
		});

		const loccode = this.userData?.user?.defaultlocation?.loccode;
		if (loccode) {
			this.globalsService.getLocationDefaults({ loccode }).subscribe((results: any) => {
				this.defaults = { ...this.defaults, ...results };
				this.overlayRegionalDefaultsFromConfig();
				this.updateBranch();
			});
		} else {
			this.overlayRegionalDefaultsFromConfig();
			this.updateBranch();
		}

		this.awaitingSearch.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((v) => this.searchAwait(v));
		this.deniedSearch.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((v) => this.searchDenied(v));
	}

	ngOnDestroy(): void {
		this.branchFormSubs.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}

	refreshApprovalList(): void {
		this.listRefreshLoading = true;
		this.systemService
			.getWebCustomers()
			.pipe(finalize(() => (this.listRefreshLoading = false)))
			.subscribe({
				next: (v: WebCustomerQueues) => {
					this.applyCustomerList(v);
					this.approvingNew = false;
				},
				error: () => {
					this.globalSearchService.showNotification('Could not load storefront registrations.', 'danger', 'bottom', 'right');
				},
			});
	}

	private mergeAppConfigDefaults(): void {
		const fromConfig = this.globalsService.config?.defaults;
		if (fromConfig && typeof fromConfig === 'object' && Object.keys(fromConfig).length > 0) {
			this.defaults = { ...CUSTOMER_APPROVAL_MINIMAL_FALLBACK, ...fromConfig };
		} else {
			this.defaults = { ...CUSTOMER_APPROVAL_LEGACY_FALLBACK };
		}
	}

	/**
	 * After merging `globals/locations/defaults`, re-apply regional fields from app config so the API's
	 * US-centric fallback does not override `config.defaults` (e.g. Canada for Investia).
	 */
	private overlayRegionalDefaultsFromConfig(): void {
		const cfgDef = this.config?.defaults || this.globalsService.config?.defaults;
		if (!cfgDef || typeof cfgDef !== 'object') {
			return;
		}
		if (cfgDef.country) {
			this.defaults.country = cfgDef.country;
		}
		if (cfgDef.region) {
			this.defaults.region = cfgDef.region;
		}
		if (cfgDef.currency) {
			this.defaults.currency = cfgDef.currency;
		}
	}

	/** Country name from storefront registration (`shipping` JSON on `customer`), if present. */
	private webRegistrationCountry(webCustomer: any): string | null {
		if (!webCustomer?.shipping) {
			return null;
		}
		try {
			const sh = typeof webCustomer.shipping === 'string' ? JSON.parse(webCustomer.shipping) : webCustomer.shipping;
			const name = sh?.shipping_country;
			if (name != null && String(name).trim() !== '') {
				return String(name).trim();
			}
		} catch {
			return null;
		}
		return null;
	}

	private applyCustomerList(v: WebCustomerQueues): void {
		const normalized: WebCustomerQueues = {
			awaiting: v?.awaiting ? [...v.awaiting] : [],
			denied: v?.denied ? [...v.denied] : [],
		};
		this.allcustomers = normalized;
		this.customers = {
			awaiting: [...normalized.awaiting],
			denied: [...normalized.denied],
		};
		this.awaitingSearch.setValue('', { emitEvent: false });
		this.deniedSearch.setValue('', { emitEvent: false });
		this.customersawaiting.emit(this.customers);
	}

	setForms(): void {
		this.debtorFromGroup = this.fb.group({
			name: ['', [Validators.required]],
			address1: ['', [Validators.required]],
			address2: ['', []],
			address3: ['', [Validators.required]],
			address4: [this.defaults.region, [Validators.required]],
			address5: ['', [Validators.required]],
			address6: [this.defaults.country, [Validators.required]],
			creditlimit: [this.defaults.credit, [Validators.required]],
			discount: [this.defaults.discount, [Validators.pattern('[0-9]*')]],
			pymtdiscount: [this.defaults.discount, [Validators.pattern('[0-9]*')]],
			arintrate: [this.defaults.interest],
			paymentterms: [this.defaults.terms, [Validators.required]],
			salestype: [this.defaults.salestype, [Validators.required]],
			typeid: [this.defaults.customertype, [Validators.required]],
			holdreason: [this.defaults.holdreason, [Validators.required]],
			currcode: [this.defaults.currency, [Validators.required]],
			emailstatement: [this.defaults.email_statement, [Validators.required]],
			statementemailaddress: ['', [Validators.email]],
			ediinvoices: [this.defaults.email_invoice, [Validators.required]],
			invoiceemailaddress: ['', [Validators.email]],
			customerpoline: [this.defaults.requirepo, [Validators.required]],
			cosm_license: [this.defaults.cosm_license, []],
			licExpDate: [this.defaults.licExpDate, []],
			signed_mor: [this.defaults.signed_mor, [Validators.required]],
		});
	}

	private clearBranchSubscriptions(): void {
		this.branchFormSubs.unsubscribe();
		this.branchFormSubs = new Subscription();
	}

	private patchBranchEmailValidators(): void {
		const ctrl = this.branchFormGroup?.get('email');
		if (!ctrl) {
			return;
		}
		ctrl.setValidators(customerBranchEmailValidators(this.config));
		ctrl.updateValueAndValidity();
		this.branchFormGroup.updateValueAndValidity();
	}

	updateBranch(): void {
		this.clearBranchSubscriptions();

		this.branchFormGroup = this.fb.group({
			branchcode: [this.defaults.branchcode, [Validators.required]],
			brname: [this.debtorFromGroup.controls.name.value, [Validators.required]],
			contactname: [' ', [Validators.required]],
			braddress1: [this.debtorFromGroup.controls.address1.value, [Validators.required]],
			braddress2: [this.debtorFromGroup.controls.address2.value],
			braddress3: [this.debtorFromGroup.controls.address3.value, [Validators.required]],
			braddress4: [this.debtorFromGroup.controls.address4.value, [Validators.required]],
			braddress5: [this.debtorFromGroup.controls.address5.value, [Validators.required]],
			braddress6: [this.debtorFromGroup.controls.address6.value, [Validators.required]],
			phoneno: [this.phone, [Validators.required]],
			email: ['', customerBranchEmailValidators(this.config)],
			faxno: [''],
			disabletrans: [this.defaults.disabled],
			tirefee: [this.defaults.tirefee],
			defaultlocation: [this.defaults.warehouse, [Validators.required]],
			defaultshipvia: [this.defaults.shipvia, [Validators.required]],
			area: [this.defaults.area, [Validators.required]],
			salesman: [this.defaults.salesman, [Validators.required]],
			tax_ref: [''],
			taxgroupid: [this.defaults.taxauth, [Validators.required]],
			specialinstructions: [''],
			username: [''],
			password: [''],
			lat: [],
			lng: [],
			bypass: false,
			route: ['', [Validators.required]],
			default_paymentmehod: [this.defaults.default_paymentmehod, [Validators.required]],
		});

		const s = this.pendingWebCustomer;

		if (s?.custom_field) {
			const cf = s.custom_field;
			const countryLine = this.webRegistrationCountry(s) ?? this.defaults.country;
			this.debtorFromGroup.patchValue({
				name: cf.company,
				address1: cf.address1,
				address2: cf.address2,
				address3: cf.city,
				address4: cf.address4,
				address5: cf.postcode,
				address6: countryLine,
			});
			this.branchFormGroup.patchValue({
				email: cf.email,
				username: cf.email,
				password: cf.password,
				contactname: `${s.firstname} ${s.lastname}`,
				phoneno: cf.telephone,
				brname: cf.company,
				braddress1: cf.address1,
				braddress2: cf.address2,
				braddress3: cf.city,
				braddress4: cf.address4,
				braddress5: cf.postcode,
				braddress6: countryLine,
				tax_ref: cf.taxref,
			});

			this.customerService.zipCodeCheck({ zipcode: cf.postcode }).subscribe((results: any) => {
				if (results?.length) {
					this.taxes = results;
					this.branchFormGroup.get('taxgroupid')?.setValue(results[0].taxgroupid);
				} else {
					this.taxes = this.alltaxes;
				}
			});
		}

		if (this.place) {
			this.branchFormGroup.get('lat')?.setValue(this.place.geometry.location.lat());
			this.branchFormGroup.get('lng')?.setValue(this.place.geometry.location.lng());
		}

		this.branchFormSubs.add(
			this.branchFormGroup.get('email')!.valueChanges.subscribe((newValue) => {
				const u = this.branchFormGroup.get('username')?.value;
				if (u !== newValue || u === '') {
					this.branchFormGroup.get('username')?.setValue(newValue);
				}
			}),
		);

		this.branchFormSubs.add(
			this.branchFormGroup.get('braddress5')!.valueChanges.subscribe((newValue) => {
				this.customerService
					.zipCodeCheck({
						zipcode: newValue,
						tax_ref: this.branchFormGroup.get('taxgroupid')?.value,
					})
					.subscribe((results: any) => {
						if (results?.length) {
							this.taxes = results;
							this.branchFormGroup.get('taxgroupid')?.setValue(results[0].taxgroupid);
						} else {
							this.taxes = this.alltaxes;
						}
					});
			}),
		);

		if (this.config?.env?.package === 'beauty') {
			this.branchFormGroup.get('route')?.clearValidators();
			this.branchFormGroup.get('route')?.setValue(1);
		}
		this.branchFormGroup.get('email')?.setValidators(customerBranchEmailValidators(this.config));
		this.branchFormGroup.get('email')?.updateValueAndValidity();
		this.branchFormGroup.updateValueAndValidity();

		this.branchFormSubs.add(
			this.branchFormGroup.get('tax_ref')!.valueChanges.subscribe((newvalue) => {
				if (newvalue !== '') {
					let currentValue = this.alltaxes.filter((v) => v.taxgroupid == this.branchFormGroup.get('taxgroupid')?.value)[0];
					currentValue = currentValue ? currentValue.taxgroupdescription : '';
					this.filterTaxAuths(true, currentValue);
				} else {
					this.filterTaxAuths(false, '');
				}
			}),
		);

		this.branchFormGroup.markAllAsTouched();
	}

	updateZones(_input: unknown): void {}

	selectCustomer(event: any): void {
		if (!event?.debtorno) {
			return;
		}
		this.customerService.getDebtor(event.debtorno).subscribe((results: any) => {
			const c = results?.customer;
			if (!c) {
				return;
			}
			this.debtorFromGroup.patchValue({
				name: c.name,
				address1: c.address1,
				address2: c.address2,
				address3: c.address3,
				address4: c.address4,
				address5: c.address5,
				creditlimit: c.creditlimit,
				customerpoline: c.customerpoline,
			});
			this.branchFormGroup.patchValue({
				brname: c.brname,
				braddress1: c.braddress1,
				braddress2: c.braddress2,
				braddress3: c.braddress3,
				braddress4: c.braddress4,
				braddress5: c.braddress5,
				contactname: c.contactname,
			});
		});
	}

	detailCustomer(s: any): void {
		this.detail = s;
		this.modalService.open(this.detailsRef, { ariaLabelledBy: 'modal-title', size: 'xl' }).result.then(
			() => {},
			() => {},
		);
	}

	approveCustomer(s: any): void {
		const loccode = this.userData?.user?.defaultlocation?.loccode;
		const start = (locResults: any) => {
			this.contacts = [];
			if (locResults && typeof locResults === 'object') {
				this.defaults = { ...this.defaults, ...locResults };
				this.overlayRegionalDefaultsFromConfig();
			}
			this.pendingWebCustomer = s;
			this.setForms();
			this.approvingNew = true;
			this.updateBranch();
			if (s?.custom_field) {
				this.prefillContact();
			}
		};
		if (loccode) {
			this.globalsService.getLocationDefaults({ loccode }).subscribe(start);
		} else {
			start({});
		}
	}

	getAddress(place: any): void {
		this.place = place;
		const components = place['address_components'];
		const name = place['name'];
		const phoneNum = place['formatted_phone_number'];

		const address1 = this.parseComponentShortName(components, ['street_number', 'route']);
		const address2 = this.parseComponentShortName(components, ['subpremise']);
		const address3 = this.parseComponentShortName(components, ['locality']);
		const address4 = this.parseComponentShortName(components, ['administrative_area_level_1']);
		const address5 = this.parseComponentShortName(components, ['postal_code']);
		const address6 = this.parseComponentLongName(components, ['country']);

		this.debtorFromGroup.patchValue({
			address1,
			address2,
			address3,
			address4,
			address5,
			address6,
		});
		this.branchFormGroup.patchValue({
			braddress1: address1,
			braddress2: address2,
			braddress3: address3,
			braddress4: address4,
			braddress5: address5,
			braddress6: address6,
			lat: this.place.geometry.location.lat(),
			lng: this.place.geometry.location.lng(),
		});

		if (name) {
			this.debtorFromGroup.get('name')?.setValue(name);
			this.branchFormGroup.get('brname')?.setValue(name);
		}
		if (phoneNum) {
			this.phone = phoneNum;
			this.branchFormGroup.get('phoneno')?.setValue(phoneNum);
		}
	}

	addContact(): void {
		if (!this.contactFormGroup.valid) {
			this.contactFormGroup.markAllAsTouched();
			return;
		}
		this.contacts.push({ ...this.contactFormGroup.value });
		this.contactFormGroup.reset({
			contid: 0,
			name: '',
			email: '',
			phone: '',
			role: [],
		});
	}

	private parseComponentShortName(components: any, types: string[]): string {
		const result: string[] = [];
		components.forEach((comp: any) => {
			comp.types.forEach((local: string) => {
				if (types.includes(local)) {
					result.push(comp.short_name || '');
				}
			});
		});
		return result.join(' ');
	}

	private parseComponentLongName(components: any, types: string[]): string {
		const result: string[] = [];
		components.forEach((comp: any) => {
			comp.types.forEach((local: string) => {
				if (types.includes(local)) {
					result.push(comp.long_name || '');
				}
			});
		});
		return result.join(' ');
	}

	prefillContact(): void {
		const email = this.branchFormGroup.get('email')?.value;
		const phone = this.branchFormGroup.get('phoneno')?.value;
		const name = this.branchFormGroup.get('contactname')?.value;
		if (!this.contactFormGroup) {
			return;
		}
		const row = { contid: 0, name, email, phone, role: ['OWNER'] };
		this.contacts.push(row);
		this.contactFormGroup.reset({
			contid: 0,
			name: '',
			email: '',
			phone: '',
			role: [],
		});
	}

	createCustomer(): void {
		this.logFormValidationErrors(this.branchFormGroup);
		this.logFormValidationErrors(this.debtorFromGroup);
		if (!this.branchFormGroup.valid || !this.debtorFromGroup.valid) {
			return;
		}

		const debtorfrom = { ...this.debtorFromGroup.value, webcustomer: true };

		this.customerService.createCustomer(debtorfrom, this.branchFormGroup.value, this.contacts).subscribe((results: any) => {
			if (results.success) {
				this.finishApprovalAfterCreate(results);
			} else {
				const bypass = confirm(results.message);
				if (bypass) {
					this.branchFormGroup.get('bypass')?.setValue(true);
					this.customerService.createCustomer(this.debtorFromGroup.value, this.branchFormGroup.value).subscribe((v: any) => {
						this.finishApprovalAfterCreate(v);
					});
				}
			}
		});
	}

	private finishApprovalAfterCreate(results: any): void {
		this.customer_created = results.customer;
		this.approvingNew = false;
		this.globalSearchService.showNotification('Customer approved and created.', 'success', 'bottom', 'right');
		const data = { customer: this.pendingWebCustomer?.customer_id };
		this.systemService.approveCustomer(data).subscribe((v) => {
			this.applyCustomerList(v);
			this.contactFormGroup?.reset();
			this.debtorFromGroup.reset();
			this.branchFormGroup.reset();
		});
	}

	denyCustomer(s: any): void {
		this.systemService.denyCustomer({ customer: s.customer_id }).subscribe((v) => {
			this.applyCustomerList(v);
			this.approvingNew = false;
		});
	}

	undenyCustomer(s: any): void {
		this.systemService.undenyCustomer({ customer: s.customer_id }).subscribe((v) => {
			this.applyCustomerList(v);
			this.approvingNew = false;
		});
	}

	searchAwait(term: string): void {
		const base = this.allcustomers;
		if (!term?.trim()) {
			this.customers = {
				awaiting: [...(base.awaiting || [])],
				denied: [...(base.denied || [])],
			};
			return;
		}
		this.customers = {
			...base,
			awaiting: this.globalSearchService.filterItem([...(base.awaiting || [])], term, 'firstname,lastname'),
			denied: [...(base.denied || [])],
		};
	}

	searchDenied(term: string): void {
		const base = this.allcustomers;
		if (!term?.trim()) {
			this.customers = {
				awaiting: [...(base.awaiting || [])],
				denied: [...(base.denied || [])],
			};
			return;
		}
		this.customers = {
			...base,
			awaiting: [...(base.awaiting || [])],
			denied: this.globalSearchService.filterItem([...(base.denied || [])], term, 'firstname,lastname'),
		};
	}

	assignCustomer(custRecord: any, existingCust: any): void {
		const newAssign = { custdata: custRecord, oldcust: existingCust };
		this.isAssigning = true;
		this.systemService.assignCustomer(newAssign).subscribe({
			next: () => {
				this.isAssigning = false;
				this.globalSearchService.showNotification(
					`Login assigned to ${existingCust.debtorno}.${existingCust.branchcode}.`,
					'success',
					'bottom',
					'right',
				);
				this.systemService.approveCustomer({ customer: custRecord.customer_id }).subscribe((v) => {
					this.applyCustomerList(v);
					this.modalService.dismissAll();
				});
			},
			error: () => {
				this.isAssigning = false;
			},
		});
	}

	assignCustomerDialog(custRecord: any): void {
		this.detail = custRecord;
		this.custresults = [];
		this.searchVal.setValue('');
		this.modalService.open(this.approveRef, { ariaLabelledBy: 'modal-title', size: 'xl' }).result.then(
			() => {},
			() => {},
		);
	}

	custSearch(): void {
		this.searching = true;
		this.customerService.getCustomerSearch({ keywords: this.searchVal.value }).subscribe({
			next: (r) => {
				this.custresults = r || [];
				this.searching = false;
			},
			error: () => {
				this.custresults = [];
				this.searching = false;
			},
		});
	}

	private logFormValidationErrors(form: UntypedFormGroup): void {
		form.markAllAsTouched();
		Object.keys(form.controls).forEach((key) => {
			const controlErrors: ValidationErrors | null = form.get(key)?.errors;
			if (controlErrors) {
				Object.keys(controlErrors).forEach((keyError) => {
					const label = key === 'reference' ? 'PO Number' : key;
					this.globalSearchService.showNotification(`${label} ${keyError}`, 'danger', 'bottom', 'right');
				});
			}
		});
	}

	compareAddress(): void {
		if (!this.debtorFromGroup.valid) {
			return;
		}
		const a1 = this.debtorFromGroup.get('address1')?.value;
		const a2 = this.debtorFromGroup.get('address2')?.value;
		const a3 = this.debtorFromGroup.get('address3')?.value;
		if (a1 === '' && a2 === '' && a3 === '') {
			return;
		}
		this.customerService.addressCompare({ add1: a1, add2: a2, add3: a3 }).subscribe((result) => {
			if (result?.length) {
				this.duplicatewarning = result;
				this.modalService.open(this.detailsModalEle, { ariaLabelledBy: 'modal-basic-title', size: 'lg', backdrop: 'static', keyboard: false }).result.then(
					() => {},
					() => {
						if (this.modalAction) {
							this.updateBranch();
							this.step();
						}
						this.modalAction = undefined;
					},
				);
			} else {
				this.updateBranch();
				this.step();
			}
		});
	}

	isMatch(entered: string, found: string): boolean {
		return entered?.toLowerCase() === found?.toLowerCase();
	}

	userSelection(input: boolean): void {
		this.modalAction = input;
		this.modalService.dismissAll();
	}

	removeContact(contact: any): void {
		const index = this.contacts.indexOf(contact);
		if (index >= 0) {
			this.contacts.splice(index, 1);
		}
	}

	step(): void {
		document.getElementById('stepperNext')?.click();
	}

	cancel(): void {
		this.debtorFromGroup.reset();
		this.branchFormGroup.reset();
		this.contactFormGroup?.reset();
		this.approvingNew = false;
		this.pendingWebCustomer = null;
	}

	filterTaxAuths(filter: boolean, current: string): void {
		if (filter) {
			let newvalue = '';
			if (current !== '') {
				newvalue = current.replace('1', '2');
			}
			const results = this.taxes.filter((r: any) => {
				if (r.taxgroupdescription?.startsWith('2')) {
					if (newvalue !== '' && newvalue === r.taxgroupdescription) {
						this.branchFormGroup.get('taxgroupid')?.setValue(r.taxgroupid);
					}
					return r;
				}
				return false;
			});
			this.taxes = results;
		} else {
			this.taxes = this.alltaxes;
		}
	}
}
